import { ScrollButton } from "@/features/MyClub/Recommendations/ScrollElements";
import { pauseIcon, playIcon } from "@/figmaElements";
import { cn } from "@/lib/utils";
import { Subscription, userSubscriptionSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { GettingReadyFc26Banner } from "./GettingReadyFc26Banner/GettingReadyFc26Banner";
import { MyClubBanner } from "./MyClubBanner/MyClubBanner";
import { MyEvoBanner } from "./MyEvoBanner/MyEvoBanner";
import { ProfitBanner } from "./ProfitBanner/ProfitBanner";

const TRANSITION_DURATION = 500;
const SWIPE_THRESHOLD = 50; // Minimum distance for a swipe to be registered

export const RootBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const intervalRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchEndRef = useRef({ x: 0, y: 0 });
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isProcessingTouchRef = useRef(false);
  const subscription = useSelector(userSubscriptionSelector);
  const canUseClubImport = useMemo(
    () => subscription === "gold" || subscription === "diamond",
    [subscription],
  );
  const isAutoPlayRef = useRef(isAutoPlay);

  useEffect(() => {
    isAutoPlayRef.current = isAutoPlay;
  }, [isAutoPlay]);

  const handleLeftClick = useCallback(() => {
    setCurrentIndex((prev) => {
      // Ensure we don't go below 0, wrap around to the end instead
      const newIndex = prev - 1;
      return newIndex < 0 ? 6 : newIndex; // 6 is images.length - 1 based on the structure
    });
    stopInterval();
    if (isAutoPlayRef.current) {
      startInterval();
    }
  }, [isAutoPlay]);

  const handleRightClick = useCallback(() => {
    setCurrentIndex((prev) => {
      // Ensure we don't go above the maximum index, wrap around to the beginning instead
      const newIndex = prev + 1;
      return newIndex > 6 ? 0 : newIndex; // 6 is images.length - 1 based on the structure
    });
    stopInterval();
    if (isAutoPlayRef.current) {
      startInterval();
    }
  }, []);

  // Touch gesture handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current.x || !touchEndRef.current.x) return;

    // Prevent rapid swiping by checking if we're already processing a touch
    if (isProcessingTouchRef.current) return;

    const deltaX = touchStartRef.current.x - touchEndRef.current.x;
    const deltaY = Math.abs(touchStartRef.current.y - touchEndRef.current.y);

    // Only register horizontal swipes if vertical movement is minimal
    if (Math.abs(deltaX) > SWIPE_THRESHOLD && deltaY < SWIPE_THRESHOLD) {
      // Set processing flag to prevent rapid swipes
      isProcessingTouchRef.current = true;

      if (deltaX > 0) {
        // Swipe left - go to next
        handleRightClick();
      } else {
        // Swipe right - go to previous
        handleLeftClick();
      }

      // Reset processing flag after the transition duration to allow the next swipe
      setTimeout(() => {
        isProcessingTouchRef.current = false;
      }, TRANSITION_DURATION + 100);
    }

    // Reset touch references
    touchStartRef.current = { x: 0, y: 0 };
    touchEndRef.current = { x: 0, y: 0 };
  }, [handleLeftClick, handleRightClick]);

  const originalImages = [
    <GettingReadyFc26Banner // Show to ALL users
      onClickLeft={handleLeftClick}
      onClickRight={handleRightClick}
    />,
    <ProfitBanner
      onClickLeft={handleLeftClick}
      onClickRight={handleRightClick}
    />,
    <MyEvoBanner
      onClickLeft={handleLeftClick}
      onClickRight={handleRightClick}
    />,
    <MyClubBanner
      onClickLeft={handleLeftClick}
      onClickRight={handleRightClick}
    />,
  ];

  const shuffledImages = useMemo(() => {
    const arr = [...originalImages];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [subscription]);

  const images = [...shuffledImages, ...shuffledImages.slice(0, 2)];

  const startInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // Ensure we don't go above the maximum index, wrap around to the beginning instead
        const newIndex = prevIndex + 1;
        return newIndex > 6 ? 0 : newIndex; // 6 is images.length - 1 based on the structure
      });
    }, 6000);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleDotClick = (index: number) => {
    stopInterval();
    setCurrentIndex(index + 1);
    if (isAutoPlay) {
      startInterval();
    }
  };

  useEffect(() => {
    startInterval();
    return () => stopInterval();
  }, []);

  // Handle infinite loop transitions
  useEffect(() => {
    if (currentIndex === 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(images.length - 2);
      }, TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
    if (currentIndex === images.length - 1) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(1);
      }, TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, images.length]);

  // Safety check to ensure currentIndex is always within bounds
  useEffect(() => {
    if (currentIndex < 0 || currentIndex >= images.length) {
      console.warn(`Invalid currentIndex: ${currentIndex}, resetting to 1`);
      setCurrentIndex(1);
    }
  }, [currentIndex, images.length]);

  // Re-enable transitions after infinite loop jump
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  return (
    <div
      className="mx-auto w-[100%] max-w-content"
      // onMouseEnter={stopInterval}
      // onMouseLeave={startInterval}
    >
      {canUseClubImport ? (
        <GettingReadyFc26Banner // Show to ALL users
          onClickLeft={handleLeftClick}
          onClickRight={handleRightClick}
        />
      ) : (
        <>
          <div
            className="relative overflow-hidden shadow-2xl w-full"
            onTouchStart={isTouchDevice ? handleTouchStart : undefined}
            onTouchMove={isTouchDevice ? handleTouchMove : undefined}
            onTouchEnd={isTouchDevice ? handleTouchEnd : undefined}
          >
            <div
              className="flex"
              style={{
                transform: `translateX(-${currentIndex * 100}%)`,
                transition: isTransitioning
                  ? `transform ${TRANSITION_DURATION}ms ease-in-out`
                  : "none",
              }}
            >
              {images.map((banner, index) => (
                <div className="w-full flex-shrink-0 object-cover" key={index}>
                  {banner}
                </div>
              ))}
            </div>
          </div>
          <div className="bottom-5 my-4 flex justify-center gap-2">
            <button
              className="flex gap-x-[3px]"
              onClick={() => {
                if (isAutoPlay) {
                  setIsAutoPlay(false);
                  stopInterval();
                } else {
                  setIsAutoPlay(true);
                  startInterval();
                }
              }}
            >
              {isAutoPlay ? pauseIcon : playIcon}
            </button>
            {originalImages.map((_, index) => {
              const activeIndex =
                (currentIndex - 1 + originalImages.length) %
                originalImages.length;
              return (
                <button
                  className="h-3.5"
                  onClick={() => handleDotClick(index)}
                  key={index}
                >
                  <div
                    className={cn(
                      "w-10 rounded-full transition-all duration-500 ease-in-out h-1",
                      activeIndex === index
                        ? "bg-primary-action"
                        : "bg-new-gray-450",
                    )}
                  />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export const CTAButton = ({
  onClick,
  subscription,
  label: labelProp = "Subscribe Now",
}: {
  onClick: () => void;
  subscription?: Subscription;
  label?: string;
}) => {
  if (subscription === "diamond") {
    return <div className="h-[56px]" />;
  }
  const label = useMemo(() => {
    if (subscription) {
      return "Upgrade Now";
    }
    return labelProp;
  }, [subscription, labelProp]);

  return (
    <button
      onClick={onClick}
      className="mx-auto w-[280px] h-[56px] rounded bg-primary-dark-blue text-white font-bold text-xl transition-all duration-200 ease-in-out transform hover:scale-102 hover:bg-primary-blue active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-dark-blue/40"
    >
      {label}
    </button>
  );
};

export const BannerStructure = ({
  title,
  children,
  bgUrl,
  learnMoreLink,
  onClickCTA,
  onClickLeft,
  onClickRight,
}: {
  children: React.ReactNode;
  bgUrl: string;
  title: React.ReactNode;
  learnMoreLink?: string;
  onClickCTA: () => void;
  onClickLeft?: () => void;
  onClickRight?: () => void;
}) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const subscription = useSelector(userSubscriptionSelector);
  const canUseClubImport = useMemo(
    () => subscription === "gold" || subscription === "diamond",
    [subscription],
  );

  if (isTouchDevice) {
    return (
      <article
        className={cn(
          "w-full relative bg-cover bg-center bg-no-repeat flex flex-col justify-end items-center",
          bgUrl,
          subscription === "diamond" ? "h-50" : "h-96",
        )}
      >
        {children}
        <div
          className={cn(
            "w-[350px] h-40 flex flex-col justify-center items-center gap-y-4 mb-3 z-10",
            subscription === "diamond" ? "h-20" : "h-40",
          )}
        >
          <img
            src={import.meta.env.VITE_APP_ASSETS_BASE_URL + "/easysbc1.png"}
            alt="EasySBC"
            className="hidden touch-device:block"
          />
          {subscription !== "diamond" && (
            <>
              <div className="flex flex-col">
                <p className="text-lg font-bold text-center">{title}</p>
                {learnMoreLink && (
                  <NavLink
                    className="text-base text-center text-dark-tertiary italic underline w-fit mx-auto"
                    to={learnMoreLink}
                  >
                    Learn More
                  </NavLink>
                )}
              </div>
              <CTAButton onClick={onClickCTA} subscription={subscription} />
            </>
          )}
        </div>
      </article>
    );
  } else {
    return (
      <article
        className={cn(
          "w-full relative bg-cover bg-center bg-no-repeat flex flex-col overflow-hidden group",
          subscription === "diamond" ? "h-60" : "h-90",
          bgUrl,
        )}
      >
        {!canUseClubImport && (
          <>
            <div className="hidden touch-device:block absolute top-1/2 -translate-y-1/2 left-4 z-1 group-hover:opacity-100 opacity-0 transition-opacity duration-300">
              <ScrollButton direction="left" onClick={onClickLeft} />
            </div>
            <div className="hidden touch-device:block absolute top-1/2 -translate-y-1/2 right-4 z-1 group-hover:opacity-100 opacity-0 transition-opacity duration-300">
              <ScrollButton direction="right" onClick={onClickRight} />
            </div>
          </>
        )}
        {children}
        <div className="w-[380px] h-[228px] ml-[130px] mt-[80px] mb-[52px] flex flex-col justify-between z-1">
          <img
            src={import.meta.env.VITE_APP_ASSETS_BASE_URL + "/easysbc1.png"}
            alt="EasySBC"
            className="h-[50px] w-auto mx-auto hidden touch-device:block"
          />
          {subscription !== "diamond" && (
            <>
              <div className="flex flex-col">
                <p className="text-xl font-bold text-center">{title}</p>
                {learnMoreLink && (
                  <NavLink
                    className="text-lg text-center text-dark-tertiary italic underline w-fit mx-auto"
                    to={learnMoreLink}
                    state={{ from: "home" }}
                  >
                    Learn More
                  </NavLink>
                )}
              </div>
              <CTAButton onClick={onClickCTA} subscription={subscription} />
            </>
          )}
        </div>
      </article>
    );
  }
};
